<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around fit-content mx-auto ga-5" ref="outerDiv">
    <ConnectingLine
      :startElementId="`query-${system.index}-${event.index}`"
      :endElementId="`role-${system.index}-${system.roles[event.index].id}`"
      :refreshKey="refreshKey"
      offsetParentId="domain-model"
    />
    <div
      :id="`query-${system.index}-${event.index}`"
      :class="{
        'model-card-border': system.queries[event.index].length > 1,
        'model-card-border-hover': !!system.queries[event.index].length
      }"
      :style="{ '--border-color': system.queries[event.index].length ? cardTypeColor(queryCardType) : '' }"
      class="w-100"
    >
      <div v-if="system.queries[event.index].length" ref="queryEl">
        <template v-for="(query, queryIndex) in queriesByBoundedContext" :key="`${query.id}`">
          <ConnectingLine
            v-if="!query.divider"
            :startElementId="`query-${system.index}-${event.index}-${queryIndex}`"
            :endElementId="`query-${system.index}-${event.index}-${queryIndex + 1}`"
            :refreshKey="refreshKey"
            direction="vertical"
            offsetParentId="domain-model"
          />
          <domain-model-query-card
            :query="query"
            :queryIndex="queryIndex"
            :system="system"
            :event="event"
            :showEntities="showEntities"
            :descriptions="descriptions"
          />
        </template>
      </div>
      <v-btn
        v-else-if="queryCardType"
        class="add-card-btn"
        variant="elevated"
        block
        @click="createCard(event.id, queryCardType.id, `<p>${queryCardType.title} for ${event.description}</p>`)"
        :style="{ '--border-color': cardTypeColor(queryCardType) }"
        :id="`query-${system.index}-${event.index}-${- 1}`"
        :disabled="store.state.isWorkflowDisabled"
      >
        + {{ queryCardType.title }}
      </v-btn>
      <aggregate-type-selector
        v-else
        :requirementTypes="requirementTypes"
        type="read model"
      />
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, nextTick, watch, ref } from 'vue'
import ConnectingLine from '@/components/ConnectingLine'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import DomainModelQueryCard from '@/components/DomainModelQueryCard.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { sanitizeCardDescription } from '@/utils/sanitize'

const {
  system,
  event,
  refreshKey,
  showEntities
} = defineProps({
  system: Object,
  event: Object,
  refreshKey: Number,
  showEntities: Boolean
})

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(requirementTypes.value))

const { cardTypeColor, createCard } = useDomainModel()

const queryEl = ref(null)
const descriptions = ref(null)

const queriesByBoundedContext = computed(() => {
  return system.queries[event.index].map((query) => {
    let isInContext = false

    if (!query.entityId || !system.groupedBy?.boundedContext) {
      isInContext = true
    }

    const entity = (workflow.value.dataModels ?? []).find((item) => item.id === query.entityId)

    if (!entity || entity.boundedContext === system.groupedBy?.boundedContext) {
      isInContext = true
    }

    return { ...query, isInContext }
  })
})

watch(() => system.queries, async () => {
  await nextTick()
  descriptions.value = system.queries.map((event) => event.map((query) => sanitizeCardDescription(query.description)))
}, { deep: true, immediate: true })
</script>

<style scoped>
.model-card-border {
  border: 1px dashed var(--border-color);
  border-radius: 8px;
  padding: 8px;
}

:deep(.model-card-border-hover) {
  .add-more-query-btn {
    transition: all 0.2s;
    opacity: 0;
  }

  &:hover {
    .add-more-query-btn {
      opacity: 1;
    }
  }
}
</style>
